import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { formatDistanceToNow } from 'date-fns';
import { INotification } from 'src/app/types';
import NotificationIcon from './NotificationIcon';
import { useStore } from 'zustand';
import notificationsSlice from '../../store/notificationsSlice';

interface NotificationCardProps {
  className?: string;
  onClose?: (id: number) => void;
  notification: INotification;
  counter?: number;
}

function NotificationCard(props: NotificationCardProps) {
  const state = useStore(notificationsSlice);
  const { notification, className, counter = 0 } = props;
  const variant: string = '';

  const handleClose = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (props.onClose) {
      props.onClose(notification.id);
    }
  };

  return (
    <Card
      className={clsx(
        'flex items-center relative w-full rounded-16 min-h-64 shadow space-x-8',
        variant === 'success' && 'bg-green-600 text-white',
        variant === 'info' && 'bg-blue-700 text-white',
        variant === 'error' && 'bg-red-600 text-white',
        variant === 'warning' && 'bg-orange-600 text-white',
        className,
        counter > 0 ? 'p-20 pb-8' : 'p-20',
      )}
      onClick={() => {
        if (notification.linkUrl) state.closeNotificationPanel();
      }}
      elevation={0}
      component={notification.linkUrl ? NavLinkAdapter : 'div'}
      to={notification.linkUrl || undefined}
      role={notification.linkUrl ? 'button' : undefined}
    >
      <div className="flex flex-col flex-auto">
        <div className="flex items-center">
          {!notification.imageUrl && (
            <Box className="flex shrink-0 items-center justify-center w-32 h-32 mr-12 rounded-full">
              <NotificationIcon value={'info'} />
            </Box>
          )}
          {notification.imageUrl && (
            <img
              className="shrink-0 w-32 h-32 mr-12 rounded-full overflow-hidden object-cover object-center"
              src={notification.imageUrl}
              alt="Notification"
            />
          )}
          <div className="flex flex-col flex-1">
            {notification.text && (
              <Typography className="font-semibold line-clamp-1">{notification.text}</Typography>
            )}
            {notification.createdAt && (
              <Typography className="mt-8 text-sm leading-none">
                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
              </Typography>
            )}
          </div>
        </div>
        {counter > 0 && (
          <div className="w-full text-center">
            <Typography
              className="mt-8 cursor-pointer hover:underline"
              style={{
                color: '#3B45E4',
                fontWeight: 400,
                display: 'inline-block',
                width: 'fit-content',
              }}
              variant="body1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                state.openNotificationPanel();
                if (props.onClose) {
                  props.onClose(notification.id);
                }
              }}
            >
              +{counter} more updates
            </Typography>
          </div>
        )}
      </div>

      <IconButton
        disableRipple
        className="top-0 right-0 absolute p-8"
        color="inherit"
        size="small"
        onClick={handleClose}
      >
        <FuseSvgIcon size={12} className="opacity-75" color="inherit">
          heroicons-solid:x
        </FuseSvgIcon>
      </IconButton>
    </Card>
  );
}

export default NotificationCard;
